/* jobcost — the floor coating job cost sheet's numbers, pure
   (docs/SERVICE_SWITCHER_PLAN.md §13 item 22).

   One coating job against the coat rate card as it stands: what it costs you,
   what to order, what you keep at your price. Everything is the engine's own
   takeoff (lib/engine.concrete.ts) regrouped the way the owner's spreadsheet
   reads — coatings and flake with the kits to order and what's left over,
   supplies thrown away per job, wear items and diamonds, labor, the drive, the
   overhead a job carries — so the sheet and the estimate never disagree.

     coatJobCost(block, card, settings, miles)   every number for one job
     sizeRows(...)                               the same price and costs on other floors
     marginBand(m)                               good ≥ 35% · warn ≥ 20% · bad under
     summaryText(jc, name)                       the text behind "Copy job summary"
     withPackPrice / withPackSize / withCoverage / withUse / withUnitCost / withHours
                                                 a recipe line as the sheet edits it
     updateCardLine(card, list, index, next)     the card with one line replaced

   No React, no window: unit-jobcost.mjs runs it in node. */
import { computeConcreteQuote, componentsOf, systemComponentsOf, stemSqftOf } from "./engine.concrete";
import type { Block, RateCard, Component, CoatSystem, SupplyLine, LaborLine, JobExtras, PricedBlock } from "./engine.concrete";

export interface Floor { id: string; label: string; low: number; high: number }
/* the floors the website prices (concrete-website lib/pricing.ts): $6–$9, patios and porches $7–$10 */
export const FLOORS: Floor[] = [
  { id: "garage", label: "Garage", low: 6, high: 9 },
  { id: "basement", label: "Basement", low: 6, high: 9 },
  { id: "shop", label: "Shop / pole barn", low: 6, high: 9 },
  { id: "patio", label: "Patio & porch", low: 7, high: 10 },
  { id: "commercial", label: "Commercial", low: 6, high: 9 },
];
export const floorOf = (id: any): Floor => FLOORS.find((f) => f.id === id) || FLOORS[0];
/* the three price chips: the floor's low, middle and high */
export const rateChipsFor = (f: Floor) => [{ label: "Low", rate: f.low }, { label: "Middle", rate: round2((f.low + f.high) / 2) }, { label: "High", rate: f.high }];
/* moving between floors keeps the same spot in the band — a low stays a low */
export function rateAcrossFloors(rate: number, from: Floor, to: Floor): number | null {
  if (rate === from.low) return to.low;
  if (rate === from.high) return to.high;
  if (rate === round2((from.low + from.high) / 2)) return round2((to.low + to.high) / 2);
  return null;
}

const round2 = (n: any) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v: any, d = 0) => { const n = Number(v); return isFinite(n) ? n : d; };

export type MarginBand = "good" | "warn" | "bad";
export const marginBand = (m: number): MarginBand => (m >= 0.35 ? "good" : m >= 0.2 ? "warn" : "bad");

/* where a recipe line lives on the card: the shared list, or one finish's layers */
export type LineList = "card" | string;
export interface SheetLine {
  list: LineList; index: number;      // → updateCardLine
  comp: Component;
  supply: SupplyLine | null;          // the takeoff's row, null when the line doesn't apply to this job (or measures zero)
  labor: LaborLine | null;            // for a labor line
}
export interface OrderRow {
  label: string; need: number; uom: string;
  packs: number | null; packSize: number | null; packUnit: string | null; have: number | null; leftover: number | null;
  packCost: number | null;            // the packs at the pack price (null: no pack price — charged at what it uses)
  charged: number;                    // what this job consumes, at the unit cost
}
export interface CostPart { label: string; value: number; color: string }
export interface JobCost {
  block: Block; card: RateCard; sys: CoatSystem | null; floor: Floor;
  floorSqft: number; stemSqft: number; sqft: number;
  rate: number; minimum: number; byRate: number; price: number; minApplies: boolean; needsRate: boolean;
  lines: SheetLine[];
  materials: { lines: SheetLine[]; total: number; tax: number; shipping: number; all: number };   // the coatings, flake and filler; `all` adds the tax paid and the shipping
  consumables: { lines: SheetLine[]; total: number };                                              // thrown away per job, the wear share, the diamonds
  equipment: { lines: SheetLine[]; total: number };
  labor: { lines: SheetLine[]; hours: number; cost: number; crewRate: number; headcount: number; personHours: number; wageEach: number };   // hours = the crew's hours on site (the engine's crew-hours), cost = hours × the crew's loaded rate
  extras: JobExtras;
  cost: number; profit: number; margin: number; breakEven: number; perHour: number;
  parts: CostPart[]; breakdown: { label: string; value: number }[];
  order: { rows: OrderRow[]; total: number; charged: number };
  time: PricedBlock["time"] | null; timeText: string;
  result: any;
}

const COLORS = { materials: "#C6821C", supplies: "#8A6B4E", labor: "#7C8A99", overhead: "#A9A8A3", profit: "#3E7B3A" };

/* every number for one coating job. `miles` is the round trip (the quote's
   inputs.concrete.miles); the card is the coat card as it stands. */
export function coatJobCost(block: Block, card: RateCard, settings: any, miles?: number): JobCost {
  const b: Block = { ...(block || ({} as Block)), jobType: "coat" };
  const s = settings || {};
  const result = computeConcreteQuote({ blocks: [b], miles: Math.max(0, num(miles)) }, { coat: card }, s);
  const pb: PricedBlock = result.concrete.blocks[0];
  const systems = card.systems || [];
  const sys = systems.find((x) => x.id === b.system) || systems[0] || null;
  const floorSqft = Math.max(0, num(b.sqft)), stemSqft = stemSqftOf(b), sqft = round2(floorSqft + stemSqft);
  // the recipe lines in the takeoff's order, each tied back to its place on the card
  const shared = componentsOf(card), layers = systemComponentsOf(sys || undefined);
  const comps: { list: LineList; index: number; comp: Component }[] = [
    ...shared.map((comp, index) => ({ list: "card" as LineList, index, comp })),
    ...layers.map((comp, index) => ({ list: (sys ? sys.id : "sys") as LineList, index, comp })),
  ];
  const lines: SheetLine[] = comps.map((c, i) => {
    const key = pb.key + "-" + (c.comp.id || i);
    const supply = [...pb.supplies, ...pb.equipment].find((r) => r.key === key) || null;
    const labor = c.comp.kind === "labor" ? pb.labor.find((l) => l.label === c.comp.desc) || null : null;
    return { ...c, supply, labor };
  });
  const of = (kind: Component["kind"]) => lines.filter((l) => l.comp.kind === kind);
  const sum = (ls: SheetLine[]) => round2(ls.reduce((t, l) => t + (l.supply ? l.supply.total : 0), 0));
  const matLines = of("material"), conLines = of("consumable"), eqLines = of("equipment"), labLines = of("labor");
  const extras: JobExtras = result.concrete.extras;
  const materials = { lines: matLines, total: sum(matLines), tax: extras.supplyTax, shipping: extras.shipping, all: round2(sum(matLines) + extras.supplyTax + extras.shipping) };
  const consumables = { lines: conLines, total: sum(conLines) };
  const equipment = { lines: eqLines, total: sum(eqLines) };
  const headcount = Math.max(1, num(result.concrete.headcount) || 1);
  const labor = { lines: labLines, hours: pb.laborHours, cost: pb.laborCost, crewRate: num(result.crewRate), headcount, personHours: round2(pb.laborHours * headcount), wageEach: round2(num(result.crewRate) / headcount) };
  const price = pb.amount, cost = result.operatorCost;
  const profit = round2(price - cost);
  const margin = price > 0 ? profit / price : 0;
  const suppliesAndWear = round2(consumables.total + equipment.total);
  const travelAndOverhead = round2(extras.travel + extras.overhead);
  const parts: CostPart[] = [
    { label: "Materials", value: materials.all, color: COLORS.materials },
    { label: "Supplies and wear", value: suppliesAndWear, color: COLORS.supplies },
    { label: "Labor", value: labor.cost, color: COLORS.labor },
    { label: "Travel and overhead", value: travelAndOverhead, color: COLORS.overhead },
    { label: "Profit", value: Math.max(0, profit), color: COLORS.profit },
  ];
  const breakdown = [
    { label: "Coatings, flake, filler" + (materials.tax > 0 || materials.shipping > 0 ? (materials.tax > 0 && materials.shipping > 0 ? ", tax, shipping" : materials.tax > 0 ? ", tax" : ", shipping") : ""), value: materials.all },
    { label: "Supplies", value: consumables.total },
    ...(equipment.total > 0 ? [{ label: "Equipment", value: equipment.total }] : []),
    { label: `Labor (${labor.hours.toFixed(1)} crew hrs)`, value: labor.cost },
    { label: "Travel", value: extras.travel },
    { label: "Overhead", value: extras.overhead },
  ];
  // the order: each coating at its pack, what's left over carries to the next job
  const rows: OrderRow[] = matLines.filter((l) => l.supply && l.supply.qty > 0).map((l) => {
    const r = l.supply!;
    const pack = l.comp.pack && num(l.comp.pack.size) > 0 ? l.comp.pack : null;
    const have = pack && r.packs != null ? round2(r.packs * num(pack.size)) : null;
    return { label: r.label, need: r.qty, uom: r.uom, packs: r.packs, packSize: pack ? num(pack.size) : null, packUnit: pack ? pack.unit : null, have, leftover: have != null ? round2(have - r.qty) : null, packCost: r.packCost, charged: r.total };
  });
  const orderGoods = round2(rows.reduce((t, r) => t + (r.packCost != null ? r.packCost : r.charged), 0));
  const order = { rows, total: round2(orderGoods * (1 + extras.supplyTaxPct / 100) + extras.shipping), charged: materials.all };
  return {
    block: b, card, sys, floor: floorOf(b.floor),
    floorSqft, stemSqft, sqft,
    rate: pb.rate, minimum: num(card.minimum), byRate: pb.byArea, price, minApplies: pb.minimumApplied, needsRate: pb.needsRate,
    lines, materials, consumables, equipment, labor, extras,
    cost, profit, margin, breakEven: sqft > 0 ? round2(cost / sqft) : 0, perHour: labor.hours > 0 ? round2(profit / labor.hours) : 0,
    parts, breakdown, order,
    time: pb.time || null, timeText: result.concrete.timeText || "",
    result,
  };
}

/* the sizes the table compares, this job's floor among them */
export const SIZES = [200, 250, 288, 400, 576, 864, 1152];
export function sizesFor(floorSqft: number): number[] {
  const here = Math.round(Math.max(0, num(floorSqft)));
  const out = SIZES.slice();
  if (here > 0 && out.indexOf(here) < 0) out.push(here);
  return out.sort((a, b) => a - b);
}
export function sizeLabel(a: number): string {
  return a === 288 ? "1-car, 288" : a === 576 ? "2-car, 576" : a === 864 ? "3-car, 864" : a === 1152 ? "4-car, 1,152" : a.toLocaleString("en-US");
}
export interface SizeRow { sqft: number; label: string; here: boolean; price: number; minApplies: boolean; cost: number; profit: number; margin: number; band: MarginBand }
/* the same rate, minimum and per-job costs on other floors — no stem wall */
export function sizeRows(block: Block, card: RateCard, settings: any, miles?: number): SizeRow[] {
  const here = Math.round(Math.max(0, num(block && block.sqft)));
  return sizesFor(here).map((a) => {
    const jc = coatJobCost({ ...block, sqft: a, stemLf: 0, stemIn: 0 }, card, settings, miles);
    return { sqft: a, label: sizeLabel(a), here: a === here, price: jc.price, minApplies: jc.minApplies, cost: jc.cost, profit: jc.profit, margin: jc.margin, band: marginBand(jc.margin) };
  });
}

const money = (n: number) => { const r = Math.round(n); return (r < 0 ? "−$" : "$") + Math.abs(r).toLocaleString("en-US"); };
const money2 = (n: number) => "$" + round2(n).toFixed(2);
const fmtRate = (n: number) => (Number.isInteger(n) ? "$" + n : "$" + n.toFixed(2));
const pct = (m: number) => Math.round(m * 100) + "%";

/* the text behind "Copy job summary" */
export function summaryText(jc: JobCost, name?: string): string {
  const order = jc.order.rows.filter((r) => r.packs != null).map((r) => `${r.packs} × ${fmtQty(r.packSize!)} ${r.packUnit} ${shortLabel(r.label)}`);
  return [
    `${(name || "").trim() || "Floor coating job"} (${jc.floor.label}${jc.sys ? " · " + jc.sys.name.toLowerCase() : ""})`,
    `Area: ${Math.round(jc.sqft)} sq ft${jc.stemSqft > 0 ? ` incl. ${Math.round(jc.stemSqft)} sq ft stem wall` : ""}`,
    `Price: ${money(jc.price)}${jc.minApplies ? " (minimum)" : ` at ${fmtRate(jc.rate)}/sq ft`}`,
    `Total cost: ${money(jc.cost)}  |  Profit: ${money(jc.profit)} (${pct(jc.margin)})`,
    `Break-even: ${money2(jc.breakEven)}/sq ft  |  Profit per crew hour: ${money(jc.perHour)}`,
    `Time: ${jc.labor.hours.toFixed(1)} hrs on site with ${jc.labor.headcount} (${jc.labor.personHours.toFixed(1)} person-hours)`,
    ...(order.length ? [`Order: ${order.join(", ")} (${money(jc.order.total)})`] : []),
  ].join("\n");
}
const fmtQty = (n: number) => (Math.abs(n - Math.round(n)) < 0.05 ? Math.round(n) : Math.round(n * 10) / 10).toLocaleString("en-US");
const shortLabel = (label: string) => String(label || "").split(",")[0].trim().toLowerCase();

/* ---- a recipe line as the sheet edits it. Each returns a new component; the
   unit cost follows the pack price so the two never disagree. ---- */
/* the pack price typed: the pack's cost, and $/unit from it */
export function withPackPrice(c: Component, price: number): Component {
  const pack = c.pack && num(c.pack.size) > 0 ? c.pack : null;
  const p = Math.max(0, num(price));
  if (!pack) return { ...c, cost: p };
  return { ...c, pack: { ...pack, cost: p }, cost: round2(p / num(pack.size)) };
}
/* the pack size typed: the pack price kept, $/unit follows */
export function withPackSize(c: Component, size: number, unit?: string): Component {
  const sz = Math.max(0, num(size));
  const pack = { ...(c.pack || { size: 0, unit: c.unit || "ea" }), size: sz, ...(unit != null ? { unit } : {}) };
  if (!(sz > 0)) { const { pack: _p, ...rest } = c; return rest as Component; }
  const packCost = num(pack.cost) > 0 ? num(pack.cost) : round2(num(c.cost) * sz);
  return { ...c, pack: { ...pack, cost: packCost }, cost: round2(packCost / sz) };
}
/* the pack price a line carries, or the unit cost × pack size */
export const packPriceOf = (c: Component): number | null => (c.pack && num(c.pack.size) > 0 ? (num(c.pack.cost) > 0 ? num(c.pack.cost) : round2(num(c.cost) * num(c.pack.size))) : null);
/* coverage — how far one unit goes — for a line measured per square foot (100 sq ft a gallon); null for anything else */
export function coverageOf(c: Component): number | null {
  if (!(num(c.qty) > 0)) return null;
  if (c.per === "100sqft") return round2(100 / num(c.qty));
  if (c.per === "sqft" || c.per === "floor-sqft") return round2(1 / num(c.qty));
  return null;
}
/* coverage typed: 130 sq ft a gallon → 0.77 gal per 100 sq ft */
export function withCoverage(c: Component, sqftPerUnit: number): Component {
  const cov = Math.max(0, num(sqftPerUnit));
  if (!(cov > 0)) return c;
  if (c.per === "100sqft") return { ...c, qty: round2(100 / cov) };
  if (c.per === "sqft" || c.per === "floor-sqft") return { ...c, qty: Math.round((1 / cov) * 10000) / 10000 };
  return c;
}
/* the quantity per measure typed (16.7 lb per 100 sq ft of floor → 0.167 per sq ft) */
export function withUse(c: Component, qty: number, per100 = false): Component {
  const q = Math.max(0, num(qty));
  return { ...c, qty: per100 && (c.per === "sqft" || c.per === "floor-sqft") ? Math.round((q / 100) * 10000) / 10000 : round2(q) };
}
/* the $/unit typed on a line with no pack (a set of rollers, a day of equipment) */
export const withUnitCost = (c: Component, cost: number): Component => ({ ...c, cost: Math.max(0, num(cost)), ...(c.pack && num(c.pack.size) > 0 ? { pack: { ...c.pack, cost: round2(Math.max(0, num(cost)) * num(c.pack.size)) } } : {}) });
/* the hours typed on a labor line */
export const withHours = (c: Component, hours: number): Component => ({ ...c, qty: Math.max(0, round2(hours)) });
export const withWaste = (c: Component, pct: number): Component => ({ ...c, waste: Math.max(0, round2(pct)) });

/* the card with one recipe line replaced (or removed with null). `list` is
   "card" for the shared lines or a finish's id for its layers; the list is
   materialised the way the editor does it, so a first-release card's flat
   assumptions become components the first time the sheet edits one. */
export function updateCardLine(card: RateCard, list: LineList, index: number, next: Component | null): RateCard {
  const swap = (arr: Component[]) => (next ? arr.map((c, i) => (i === index ? next : c)) : arr.filter((_, i) => i !== index));
  if (list === "card") return { ...card, components: swap(componentsOf(card)) };
  return { ...card, systems: (card.systems || []).map((s) => (s.id === list ? (({ materialPerSqFt: _m, ...rest }) => ({ ...rest, components: swap(systemComponentsOf(s)) }))(s) : s)) };
}
/* the card with one finish's $/sq ft set */
export const withSystemRate = (card: RateCard, systemId: string, rate: number): RateCard => ({ ...card, systems: (card.systems || []).map((s) => (s.id === systemId ? { ...s, rate: Math.max(0, round2(rate)) } : s)) });
